import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, articles } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-20 container-site min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 新金瓶梅`,
    description: currentCategory.description,
    url: `https://xinjinpingmei.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://xinjinpingmei.com/article/${article.id}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="container-site py-12 md:py-16">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="flex items-center gap-4 mt-2">
              <div className={`w-14 h-14 flex items-center justify-center rounded-xl ${
                currentCategory.color === 'accent' ? 'bg-accent-100 text-accent-600' :
                currentCategory.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                'bg-secondary-100 text-secondary-600'
              }`}>
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="container-site py-8">
          <div className="flex items-center gap-2 mb-8 flex-wrap">
            <span className="text-sm text-foreground-500 mr-2">切换分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}